import React, { useState, useEffect } from "react";
import Table from "@mui/joy/Table";
import { Typography, Box } from "@mui/joy";
import { db } from "../../FirebaseConfig";
import Chip from "@mui/joy/Chip";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

interface Props {
  eventname: string;
}

const EventLeaderBoardTable: React.FC<Props> = ({ eventname }) => {
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (eventname) {
      setLoading(true);

      // Firestore real-time listener using onSnapshot
      const unsubscribe = db
        .collection("Events")
        .doc(eventname.toLowerCase())
        .collection("Registrations")
        .orderBy("points", "desc")
        .onSnapshot(
          (snapshot) => {
            const leaderboardData = snapshot.docs.map((doc, index) => {
              const { username, points } = doc.data();
              return {
                rank: index + 1,
                username: username || "Unknown",
                total_score: points || 0,
              };
            });

            setLeaderboard(leaderboardData);
            setLoading(false);
          },
          (error) => {
            console.error("Error fetching real-time leaderboard data: ", error);
            setLoading(false);
          }
        );

      // Clean up the listener on component unmount
      return () => unsubscribe();
    }
  }, [eventname]);

  // Function to get the Chip component for top 3 positions
  const renderChip = (rank: number, username: string) => {
    const chipStyle = {
      backgroundColor: "",
      color: "#000",
      fontWeight: "bolder",
    };

    switch (rank) {
      case 1:
        chipStyle.backgroundColor = "rgb(255,215,0,0.7)";
        return (
          <Chip
            variant="soft"
            startDecorator={<WorkspacePremiumIcon />}
            sx={chipStyle}
          >
            {username}
          </Chip>
        );
      case 2:
        chipStyle.backgroundColor = "rgb(192,192,192,0.7)";
        return (
          <Chip
            variant="soft"
            startDecorator={<WorkspacePremiumIcon />}
            sx={chipStyle}
          >
            {username}
          </Chip>
        );
      case 3:
        chipStyle.backgroundColor = "rgb(205,127,50,0.7)";
        return (
          <Chip
            variant="soft"
            startDecorator={<WorkspacePremiumIcon />}
            sx={chipStyle}
          >
            {username}
          </Chip>
        );
      default:
        return (
          <Typography sx={{ fontWeight: "normal" }}>{username}</Typography>
        );
    }
  };

  // Function to check if the row is in top 3
  const isTopThree = (rank: number) => {
    return rank <= 3;
  };

  return (
    <>
      {loading ? (
        <Typography level="body-lg" textAlign="center">
          Loading...
        </Typography>
      ) : (
        <>
          {leaderboard.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography level="h4" textAlign="center">
                No scoreboard available
              </Typography>
            </Box>
          ) : (
            <Table aria-label="Leaderboard table" sx={{ textAlign: "center" }}>
              <thead>
                <tr>
                  <th style={{ width: "20%", textAlign: "center" }}>Rank</th>
                  <th style={{ width: "60%", textAlign: "center" }}>Name</th>
                  <th style={{ width: "20%", textAlign: "center" }}>Points</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry) => (
                  <tr
                    key={entry.rank}
                    style={{
                      fontWeight: isTopThree(entry.rank) ? "bolder" : "normal",
                    }}
                  >
                    <td>{entry.rank}</td>
                    <td>{renderChip(entry.rank, entry.username)}</td>
                    <td>{entry.total_score}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </>
  );
};

export default EventLeaderBoardTable;
